import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Home, DollarSign, Bed, Bath } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultProperty = {
  title: '',
  address: '',
  city: '',
  state: 'FL',
  zip_code: '',
  price: '',
  bedrooms: '',
  bathrooms: '',
  sqft: '',
  lot_size: '',
  year_built: '',
  property_type: 'single_family',
  listing_type: 'featured',
  description: '',
  features: [],
  images: [],
  agent_name: ''
};

const propertyTypes = ['single_family', 'condo', 'townhouse', 'multi_family', 'land', 'commercial'];
const listingTypes = ['featured', 'off_market', 'custom'];

export const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState(defaultProperty);
  const [featuresInput, setFeaturesInput] = useState('');
  const [imagesInput, setImagesInput] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties?active_only=false`);
      setProperties(response.data);
    } catch (error) {
      toast.error('Error fetching properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseFloat(formData.bathrooms) || 0,
        sqft: parseInt(formData.sqft) || 0,
        year_built: parseInt(formData.year_built) || 0,
        features: featuresInput.split(',').map(s => s.trim()).filter(Boolean),
        images: imagesInput.split('\n').map(s => s.trim()).filter(Boolean)
      };

      if (editingProperty?.id) {
        await axios.put(`${API}/properties/${editingProperty.id}`, data);
        toast.success('Property updated');
      } else {
        await axios.post(`${API}/properties`, data);
        toast.success('Property added');
      }
      setEditingProperty(null);
      setFormData(defaultProperty);
      setFeaturesInput('');
      setImagesInput('');
      fetchProperties();
    } catch (error) {
      toast.error('Error saving property');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      await axios.delete(`${API}/properties/${id}`);
      toast.success('Property deleted');
      fetchProperties();
    } catch (error) {
      toast.error('Error deleting property');
    }
  };

  const openEditModal = (property = null) => {
    if (property) {
      setEditingProperty(property);
      setFormData({
        ...property,
        price: property.price?.toString() || '',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        sqft: property.sqft?.toString() || '',
        year_built: property.year_built?.toString() || ''
      });
      setFeaturesInput(property.features?.join(', ') || '');
      setImagesInput(property.images?.join('\n') || '');
    } else {
      setEditingProperty({});
      setFormData(defaultProperty);
      setFeaturesInput('');
      setImagesInput('');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-properties">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Properties
            </h1>
            <p className="text-[#808080] mt-1">Manage featured and custom listings</p>
          </div>
          <Button className="btn-gold" onClick={() => openEditModal()} data-testid="add-property-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Property
          </Button>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : properties.length === 0 ? (
          <div className="dashboard-card text-center py-16">
            <p className="text-[#808080]">No properties yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <div key={property.id} className="dashboard-card" data-testid={`property-card-${property.id}`}>
                {property.images?.[0] && (
                  <img src={property.images[0]} alt={property.title} className="w-full h-40 object-cover rounded-lg mb-4" />
                )}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-[#F5F5F0] font-medium">{property.title}</h3>
                    <p className="text-[#808080] text-sm">{property.address}, {property.city}</p>
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => openEditModal(property)}
                      className="p-2 hover:bg-[#D4AF37]/10 rounded text-[#D4AF37]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(property.id)}
                      className="p-2 hover:bg-red-500/10 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-[#D4AF37] text-xl font-light mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {formatPrice(property.price)}
                </p>
                
                <div className="flex items-center space-x-4 text-sm text-[#808080] mb-3">
                  {property.bedrooms > 0 && (
                    <span className="flex items-center"><Bed className="w-4 h-4 mr-1" /> {property.bedrooms} bed</span>
                  )}
                  {property.bathrooms > 0 && (
                    <span className="flex items-center"><Bath className="w-4 h-4 mr-1" /> {property.bathrooms} bath</span>
                  )}
                  {property.sqft > 0 && (
                    <span>{property.sqft.toLocaleString()} sqft</span>
                  )}
                </div>
                
                <div className="flex space-x-2">
                  <span className="text-xs px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded capitalize">
                    {property.listing_type.replace('_', ' ')}
                  </span>
                  <span className="text-xs px-2 py-1 bg-[#1A1A1A] text-[#808080] rounded capitalize">
                    {property.property_type.replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit/Add Modal */}
        {editingProperty !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-[#111111] p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  {editingProperty.id ? 'Edit Property' : 'Add Property'}
                </h3>
                <button onClick={() => setEditingProperty(null)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Title *"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
                <Input
                  placeholder="Address *"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
                <Input
                  placeholder="City *"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <div className="flex space-x-2">
                  <Input
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] w-20"
                  />
                  <Input
                    placeholder="ZIP"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] flex-1"
                  />
                </div>
                <Input
                  type="number"
                  placeholder="Price *"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    placeholder="Beds"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  />
                  <Input
                    type="number"
                    placeholder="Baths"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  />
                </div>
                <Input
                  type="number"
                  placeholder="Sqft"
                  value={formData.sqft}
                  onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <Input
                  type="number"
                  placeholder="Year Built"
                  value={formData.year_built}
                  onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                />
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0]"
                >
                  {propertyTypes.map(t => (
                    <option key={t} value={t} className="capitalize">{t.replace('_', ' ')}</option>
                  ))}
                </select>
                <select
                  value={formData.listing_type}
                  onChange={(e) => setFormData({ ...formData, listing_type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0]"
                >
                  {listingTypes.map(t => (
                    <option key={t} value={t} className="capitalize">{t.replace('_', ' ')}</option>
                  ))}
                </select>
                <Textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
                <Input
                  placeholder="Features (comma separated)"
                  value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
                <Textarea
                  placeholder="Image URLs (one per line)"
                  value={imagesInput}
                  onChange={(e) => setImagesInput(e.target.value)}
                  rows={3}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
                <Input
                  placeholder="Agent Name"
                  value={formData.agent_name}
                  onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] md:col-span-2"
                />
              </div>
              <Button onClick={handleSave} className="btn-gold w-full mt-6" data-testid="save-property-btn">
                Save Property
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
